import {
  accountBalanceAtom,
  appEventDispatcher,
  AppEvents,
  currencyExchangeRateRTAtom,
  inventoryDetailsAtom,
  LogoutReason,
  ObjectSetLoadable,
  onAppEvent,
  SSESource,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthEvents } from '../shared/services/authorization/useAuthEvents';
import { SSE } from '../shared/services/sse/SSE';
import { useSseCommunicator } from '../shared/services/sse/useSseCommunicator';
import { primaryInstanceAtom } from '../shared/state/primaryInstanceAtom';
import { createLogger } from '../shared/utils/logger';
import MultiBrowserNetBrokerConfig = AppEvents.MultiBrowserNetBrokerConfig;
import OtherInstanceLoggedInEvent = AppEvents.OtherInstanceLoggedInEvent;
import SSEConnectionStateUpdateEvent = AppEvents.SSEConnectionStateUpdateEvent;

/**
 * Provider for handling SSE (e.g.: EStream) connections
 */
export const SSEConnectionProvider = ({ children }: { children: React.ReactNode }) => {
  const logger = useRef(createLogger('SSEConnectionContext'));
  const { verifyAndStoreToken, otherInstanceLoginInProgress, handleSignOff } = useAuthEvents();
  // TODO NN-3143 - TBD how to handle the window close event, when should we prevent it
  const [preventWindowClose] = useState(false);
  const [, setIsPrimaryInstance] = useAtom(primaryInstanceAtom);
  const [, setInventoryDetails] = useAtom(inventoryDetailsAtom);
  const [, setAccountBalance] = useAtom(accountBalanceAtom);
  const [, setCurrencyExchangeRates] = useAtom(currencyExchangeRateRTAtom);

  useSseCommunicator({ offlineMode: false, logger: logger.current });

  /**
   * When another instance has logged in we pass the token to the auth service
   */
  const onOtherInstanceLoggedIn = useCallback(
    async (props: MultiBrowserNetBrokerConfig) => {
      SSE.loginInitiatedFromOtherInstance = true;
      logger.current.info(`onOtherInstanceLoggedIn!`);

      SSE.sseCommunicator.userStreamConnection.setToken(props.jwtToken);
      SSE.sseCommunicator.eStreamConnection.setToken(props.jwtToken);
      appEventDispatcher.dispatchEvent(
        new SSEConnectionStateUpdateEvent({
          source: SSESource.UserStream,
          connectionState: props.userStreamConnectionState,
          isInitalValue: true,
        }),
      );
      appEventDispatcher.dispatchEvent(
        new SSEConnectionStateUpdateEvent({
          source: SSESource.EStream,
          connectionState: props.eStreamConnectionState,
          isInitalValue: true,
        }),
      );
      await verifyAndStoreToken(props.jwtToken, props.idToken, { fromOtherInstance: true });
      setInventoryDetails(props.inventoryDetails);
      setAccountBalance(new ObjectSetLoadable(accountBalance => accountBalance.accountNumber, props.accountBalance));
      setCurrencyExchangeRates(props.currencyExchangeRates);
    },
    [verifyAndStoreToken, setInventoryDetails, setAccountBalance, setCurrencyExchangeRates],
  );

  /**
   * When another instance has started to log in we set the auth state to loading
   */
  const onOtherInstanceLoginStarted = useCallback(() => {
    setIsPrimaryInstance(false);
    otherInstanceLoginInProgress();
  }, [otherInstanceLoginInProgress, setIsPrimaryInstance]);

  /**
   * When another instance has logged out we log out this instance as well
   */
  const onOtherInstanceLoggedOut = useCallback(async () => {
    // at this point we can't use useAuthorizationFlow because that hook requires another context deeper in the context tree,
    // but we can use directly the actual logout method
    handleSignOff(undefined, LogoutReason.OtherInstanceLoggedOut);
  }, [handleSignOff]);

  /**
   * - initializes the SSE Manager, which tries to decide which instance should be the primary one
   * - sets up events coming from SSE Manager
   * - handles the instance destroy event (aka browser window/tab has closed)
   * @private
   */
  const initSSE = useCallback(() => {
    window.addEventListener('beforeunload', event => {
      SSE.sseCommunicator.destroy();
      if (preventWindowClose) {
        event.preventDefault();
        return (event.returnValue = '');
      }
    });
  }, [preventWindowClose]);

  useEffect(() => {
    const listeners = [
      onAppEvent(AppEvents.OtherInstanceLoginStartedEvent.type, () => onOtherInstanceLoginStarted()),
      onAppEvent(AppEvents.OtherInstanceLoggedInEvent.type, (event: OtherInstanceLoggedInEvent) =>
        onOtherInstanceLoggedIn(event.data),
      ),
      onAppEvent(AppEvents.OtherInstanceLoggedOutEvent.type, () => onOtherInstanceLoggedOut()),
    ];

    return () => {
      listeners.forEach(l => l());
    };
  }, [onOtherInstanceLoggedIn, onOtherInstanceLoggedOut, onOtherInstanceLoginStarted]);

  /**
   * Initializes the SSE event listeners, and sets up the SSE Manager
   */
  useEffect(() => {
    initSSE();
    // React wants us to include initSSE, but we don't want to wrap every function into a useCallback
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};
